import { supabase } from '../../config/supabase';

export interface TripInvite {
  id: string;
  trip_id: string;
  invite_code: string;
  invited_by: string;
  invited_email: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  accepted_by: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
  accepted_at: string | null;
}

export interface CreateTripInviteInput {
  trip_id: string;
  invited_by: string;
  invited_email?: string;
  expires_at?: string;
}

export interface AcceptInviteResult {
  success: boolean;
  trip_id: string;
  error?: string;
}

class TripInvitesService {
  /**
   * Generate a random invite code
   */
  private generateInviteCode(): string {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed ambiguous characters
    const length = 8;
    let code = '';
    for (let i = 0; i < length; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  }

  /**
   * Create a new trip invite
   */
  async createInvite(input: CreateTripInviteInput): Promise<TripInvite> {
    const inviteCode = this.generateInviteCode();
    const expiresAt = input.expires_at || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('trip_invites' as any)
      .insert([{
        trip_id: input.trip_id,
        invite_code: inviteCode,
        invited_by: input.invited_by,
        invited_email: input.invited_email || null,
        expires_at: expiresAt,
        status: 'pending',
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating trip invite:', error);
      throw error;
    }

    return data as unknown as TripInvite;
  }

  /**
   * Get invite by code
   */
  async getInviteByCode(code: string): Promise<TripInvite | null> {
    const { data, error } = await supabase
      .from('trip_invites' as any)
      .select('*')
      .eq('invite_code', code)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching invite:', error);
      throw error;
    }

    return data as unknown as TripInvite;
  }

  /**
   * Get all invites for a trip
   */
  async getInvitesByTripId(tripId: string): Promise<TripInvite[]> {
    const { data, error } = await supabase
      .from('trip_invites' as any)
      .select('*')
      .eq('trip_id', tripId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching trip invites:', error);
      throw error;
    }

    return (data || []) as unknown as TripInvite[];
  }

  /**
   * Accept an invite and add user as trip participant
   */
  async acceptInvite(inviteCode: string, userId: string): Promise<AcceptInviteResult> {
    // Get the invite
    const invite = await this.getInviteByCode(inviteCode);

    if (!invite) {
      return {
        success: false,
        trip_id: '',
        error: 'Invite not found',
      };
    }

    // Check if invite is valid
    if (invite.status !== 'pending') {
      return {
        success: false,
        trip_id: invite.trip_id,
        error: `Invite is ${invite.status}`,
      };
    }

    if (new Date(invite.expires_at) < new Date()) {
      // Mark as expired
      await this.updateInviteStatus(invite.id, 'expired');
      return {
        success: false,
        trip_id: invite.trip_id,
        error: 'Invite has expired',
      };
    }

    try {
      // Check if user is already a participant
      const { data: existingParticipant } = await supabase
        .from('trip_participants')
        .select('*')
        .eq('trip_id', invite.trip_id)
        .eq('user_id', userId)
        .single();

      if (existingParticipant) {
        // User is already a participant, mark invite as accepted
        await this.updateInviteStatus(invite.id, 'accepted', userId);
        return {
          success: true,
          trip_id: invite.trip_id,
        };
      }

      // Add user as participant
      const { error: participantError } = await supabase
        .from('trip_participants')
        .insert([{
          trip_id: invite.trip_id,
          user_id: userId,
          role: 'participant',
        }]);

      if (participantError) {
        console.error('Error adding participant:', participantError);
        throw participantError;
      }

      // Mark invite as accepted
      await this.updateInviteStatus(invite.id, 'accepted', userId);

      return {
        success: true,
        trip_id: invite.trip_id,
      };
    } catch (error) {
      console.error('Error accepting invite:', error);
      return {
        success: false,
        trip_id: invite.trip_id,
        error: error instanceof Error ? error.message : 'Failed to accept invite',
      };
    }
  }

  /**
   * Update invite status
   */
  async updateInviteStatus(
    inviteId: string,
    status: 'pending' | 'accepted' | 'expired' | 'cancelled',
    acceptedBy?: string
  ): Promise<TripInvite> {
    const updateData: any = { status };

    if (status === 'accepted' && acceptedBy) {
      updateData.accepted_by = acceptedBy;
      updateData.accepted_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('trip_invites' as any)
      .update(updateData)
      .eq('id', inviteId)
      .select()
      .single();

    if (error) {
      console.error('Error updating invite status:', error);
      throw error;
    }

    return data as unknown as TripInvite;
  }

  /**
   * Cancel an invite
   */
  async cancelInvite(inviteId: string): Promise<void> {
    await this.updateInviteStatus(inviteId, 'cancelled');
  }

  /**
   * Delete an invite
   */
  async deleteInvite(inviteId: string): Promise<void> {
    const { error } = await supabase
      .from('trip_invites' as any)
      .delete()
      .eq('id', inviteId);

    if (error) {
      console.error('Error deleting invite:', error);
      throw error;
    }
  }

  /**
   * Get invite link URL
   */
  getInviteLink(inviteCode: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite/${inviteCode}`;
  }
}

export const tripInvitesService = new TripInvitesService();
